import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Snackbar, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import ContainerInterior from '../OrderDetailsOfd/ContainerPlanning';
import { ContainerFillResult } from '../../../app/services/api/orderManagementClient';
import { applyRoundAndFormat } from '../../../utils';
import { useTranslation } from 'react-i18next';

interface ContainerPlanningViewProps {
  containerData: ContainerFillResult | null;
  currencyCode: string;
}

const ContainerPlanningView: React.FC<ContainerPlanningViewProps> = ({ containerData, currencyCode }) => {
  const [showContainerSnackbar, setShowContainerSnackbar] = useState(false);
  const prevContainerCount = useRef<number>(0);
  const { t } = useTranslation('orderDetails');
  
  useEffect(() => {
    if (containerData) {
      const currentCount = containerData.containerFillPercents.length;
      console.log('Current container count:', currentCount);
      console.log('Previous container count:', prevContainerCount.current);
      console.log('Show container snackbar:', showContainerSnackbar);
      if (prevContainerCount.current && currentCount > prevContainerCount.current) {
        setShowContainerSnackbar(true);
      }
      prevContainerCount.current = currentCount;
    }
  }, [containerData?.containerFillPercents.length]);
 
  if (!containerData) {
    return <Typography>Loading container data...</Typography>;
  }

  const MAX_PALLETS_PER_CONTAINER = containerData.maxPalletsPerContainer;

  return (
    <Box
      display="flex"
      gap={3}
      sx={{
        width: '100%',
        overflowX: 'auto', // allow horizontal scroll
      }}
    >
      <Box
        display="flex" // <-- use "flex" not "inline-flex"
        gap={3}
        flexWrap="nowrap"
        sx={{
          minWidth: '100%',
        }}
      >
        {containerData.containerFillPercents.map((percent, index) => {
          const palletsInContainer = Math.round(
            (percent / 100) * MAX_PALLETS_PER_CONTAINER,
          );          
          const isFilledEnough = percent >= containerData.minFill;
          const isOptimallyFilled = percent >= 95;
          
          const borderColor = isOptimallyFilled ? '#4CAF50' : isFilledEnough ? '#FF9800' : '#F44336';
          const backgroundColor = isOptimallyFilled ? '#A8E6A1' : isFilledEnough ? '#FFE082' : '#F8A1A1';

          return (
            <Box
              key={index}
              display="flex"
              flexDirection="column"
              alignItems="center"
              minWidth="250px"
            >
              <Typography 
                gutterBottom>
                {t('containerPlanning.container')} #{index + 1}
              </Typography>

              <Box
                border={`4px solid ${borderColor}`}
                bgcolor={backgroundColor}
                p={2}
                mb={1}
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
              >
                <ContainerInterior palletNo={palletsInContainer} />
              </Box>

              <Table
                size="small"
                sx={{
                  borderCollapse: 'collapse',
                  '& td, & th': {
                    border: '2px solid black',
                    fontWeight: 'bold',
                  },
                  width: '100%',
                }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell>{t('containerPlanning.containerSizeFt')}</TableCell>
                    <TableCell>{containerData.containerType}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('containerPlanning.palletSize')}</TableCell>
                    <TableCell>{containerData.palletType}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('containerPlanning.palletFilled')}</TableCell>
                    <TableCell>
                      {palletsInContainer} / {MAX_PALLETS_PER_CONTAINER}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('containerPlanning.minFill')} %</TableCell>
                    <TableCell>{containerData.minFill}%</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('containerPlanning.currentFill')} %</TableCell>                    <TableCell
                      sx={{
                        backgroundColor: percent >= 95 ? '#A8E6A1' : percent >= containerData.minFill ? '#FFE082' : '#F8A1A1',
                      }}
                    >
                      {percent.toFixed(2)}%
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('containerPlanning.creditLimit')}</TableCell>
                    <TableCell>{applyRoundAndFormat(containerData.creditLimit, currencyCode)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          );
        })}
      </Box>
      <Snackbar
        open={showContainerSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowContainerSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setShowContainerSnackbar(false)}
          severity={'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {t('containerPlanning.newContainerAdded')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContainerPlanningView;
